import {
    AddNewCreditInvoiceForm,
    InputScopeType,
    MatterAmountForm,
} from 'components/modals/invoices/AddNewCreditInvoiceFormModal';
import { Checkbox } from 'components/ui/checkbox';
import cloneDeep from 'lodash/cloneDeep';
import { useCallback, useId, useMemo } from 'react';
import { Control, UseFormGetValues, UseFormSetValue, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { MatterStateType } from 'types/matters';

interface Props {
    state: string;
    count: number;
    control: Control<AddNewCreditInvoiceForm>;
    setValue: UseFormSetValue<AddNewCreditInvoiceForm>;
    getValues: UseFormGetValues<AddNewCreditInvoiceForm>;
    selectedInputScopes: InputScopeType[];
    setSelectedInputScopes: (value: InputScopeType[]) => void;
    onExpand?: () => void;
}

export const MatterAmountAccordionTitle = (props: Props): JSX.Element => {
    const { state, count, control, setValue, getValues, selectedInputScopes, setSelectedInputScopes, onExpand } = props;
    const titleId = useId();

    const [inProgress, completed, pending, offerWon, offerFailed] = useWatch({
        control,
        name: ['inProgress', 'completed', 'pending', 'offerWon', 'offerFailed'],
    });

    let title = '';
    let group: 'inProgress' | 'pending' | 'completed' | 'offerWon' | 'offerFailed' | 'archived' = 'archived';
    switch (state) {
        case MatterStateType.COMPLETED:
            title = 'contacts.cases.completed';
            group = 'completed';
            break;
        case MatterStateType.OFFER_WON:
            title = 'contacts.cases.offer-won';
            group = 'offerWon';
            break;
        case MatterStateType.OFFER_LOST:
            title = 'contacts.cases.offer-failed';
            group = 'offerFailed';
            break;
        case MatterStateType.OFFER_PENDING:
            title = 'contacts.cases.pending';
            group = 'pending';
            break;
        case MatterStateType.IN_PROGRESS:
            title = 'contacts.cases.in-progress';
            group = 'inProgress';
            break;
    }

    const groupMatters = useMemo(
        () =>
            ({ inProgress, completed, pending, offerWon, offerFailed, archived: [] as MatterAmountForm[] })[group] ??
            [],
        [inProgress, completed, pending, offerWon, offerFailed, group]
    );
    const checked = useMemo(
        () => !!groupMatters.length && groupMatters.every((matter) => matter.selected),
        [groupMatters]
    );

    const handleOnChange = useCallback(
        (checked: boolean) => {
            const matterList = getValues(group);
            const groupInputScopeList: InputScopeType[] = [];
            if (matterList?.length) {
                matterList.forEach((matter) => {
                    matter.selected = checked;
                    if (!checked) {
                        matter.amount = '';
                    }

                    const { currency, officeId, payerId } = matter;
                    if (
                        !groupInputScopeList.some(
                            (item) =>
                                item.currency === currency && item.officeId === officeId && item.payerId === payerId
                        )
                    ) {
                        groupInputScopeList.push({ currency, officeId, payerId });
                    }
                });
                setValue(group, matterList);
            }

            const scopeList = cloneDeep(selectedInputScopes);
            groupInputScopeList.forEach((inputScope) => {
                const definedScope = scopeList.find(
                    (item) =>
                        item.currency === inputScope.currency &&
                        item.officeId === inputScope.officeId &&
                        item.payerId === inputScope.payerId
                );
                const hasMoreMattersInDefinedScope = [
                    ...(inProgress ?? []),
                    ...(pending ?? []),
                    ...(completed ?? []),
                    ...(offerWon ?? []),
                    ...(offerFailed ?? []),
                ].some(
                    (item) =>
                        item.selected &&
                        item.currency === inputScope.currency &&
                        item.officeId === inputScope.officeId &&
                        item.payerId === inputScope.payerId &&
                        !matterList.map((matter) => matter.id).includes(item.id)
                );

                if (checked && !definedScope) {
                    scopeList.push(inputScope);
                } else if (!checked && definedScope && !hasMoreMattersInDefinedScope) {
                    const index = scopeList.indexOf(definedScope);
                    if (index > -1) {
                        scopeList.splice(index, 1);
                    }
                }
            });

            setSelectedInputScopes(scopeList);
        },
        [
            completed,
            getValues,
            group,
            inProgress,
            offerWon,
            offerFailed,
            pending,
            selectedInputScopes,
            setSelectedInputScopes,
            setValue,
        ]
    );

    return (
        <div className="flex items-center gap-2">
            {/* The class sits on a wrapper, not the checkbox: a click on the checkbox is re-dispatched on
                its hidden input, a sibling of the box, and the accordion's summary ignores clicks from
                inside `.accordion-checkbox`. */}
            <span className="accordion-checkbox inline-flex">
                <Checkbox
                    aria-labelledby={titleId}
                    checked={checked}
                    variant="secondary"
                    onCheckedChange={(isChecked) => {
                        handleOnChange(isChecked);
                        onExpand?.();
                    }}
                />
            </span>
            {/* Named by reference, not a <label>: a label click would also toggle the group it sits in. */}
            <span id={titleId}>
                <FormattedMessage id={title} values={{ count }} />
            </span>
        </div>
    );
};
